import { CoinflipChoice, MiniGameStatus } from '@/__generated__/graphql'
import { create } from 'zustand'
import { createComputed } from 'zustand-computed'
import { AnimationState, GAME_MODE, GameStatus } from './types'
import { BigNumber, BigNumberInBase, BigNumberInWei } from '@injectivelabs/utils'
import { getTokenByCurrency, getTokenByDenom } from '@/services/token-service'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'

type FormFields = {
  amount: string
  mode: GAME_MODE
  denom: string
  maxValue: number
  minValue: number
}

export const currenciesSettings = {
  supported: [SupportedCurrencies.INJ, SupportedCurrencies.USDC, SupportedCurrencies.NBZ, SupportedCurrencies.SOL],
  minValue: {
    [SupportedCurrencies.INJ]: 0.01,
    [SupportedCurrencies.USDC]: 0.1,
    [SupportedCurrencies.USDT]: 0.1,
    [SupportedCurrencies.NBZ]: 1,
    [SupportedCurrencies.SHU]: 1,
    [SupportedCurrencies.SOL]: 0.001,
  },
  maxValue: {
    [GAME_MODE.INSTANT]: {
      [SupportedCurrencies.INJ]: 10,
      [SupportedCurrencies.NBZ]: 1_000,
      [SupportedCurrencies.SHU]: 100,
      [SupportedCurrencies.USDC]: 100,
      [SupportedCurrencies.USDT]: 100,
      [SupportedCurrencies.SOL]: 1,
    },
    [GAME_MODE.MULTIPLY]: {
      [SupportedCurrencies.INJ]: 1,
      [SupportedCurrencies.NBZ]: 100,
      [SupportedCurrencies.SHU]: 100,
      [SupportedCurrencies.USDC]: 20,
      [SupportedCurrencies.USDT]: 20,
      [SupportedCurrencies.SOL]: 0.1,
    },
  },
  maxWin: {
    [GAME_MODE.MULTIPLY]: {
      [SupportedCurrencies.INJ]: 200,
      [SupportedCurrencies.NBZ]: 20_000,
      [SupportedCurrencies.SHU]: 10_000,
      [SupportedCurrencies.USDC]: 3_000,
      [SupportedCurrencies.USDT]: 3_000,
      [SupportedCurrencies.SOL]: 20,
    },
  },
}

type GameConfig = {
  rtp: number
  maxWin: BigNumber
  maxGameRound: number
}

type CoinflipState = {
  gameId: string | null
  gameConfig: GameConfig
  isInitialized: boolean
  status: GameStatus
  isDrawing: boolean
  roundState: RoundState
  history: Array<UserCoice & { id: string }>
  getHistory: () => Array<UserCoice & { id: string }>
  lastRoundResult?: MiniGameStatus
  setNextStep: (step: number, choice: CoinflipChoice) => void
  setGameActive: () => void
  resumeGame: (state: {
    gameId: string
    rounds: RoundState
    lastChoice: CoinflipChoice
    gameConfig: GameConfig
  }) => void
  initialize: () => void
  handleEndGame: () => void
  setGameId: (gameId: string) => void
  boot: () => void
  setDrawing: () => void
  setDrawingOff: () => void
  isWithdrawing: boolean
  setWithdrawing: () => void
  setWithdrawingOff: () => void
  setError: () => void
  formRevalidation: () => void
  addToHistory: (lastChoice: UserCoice & { id: string }) => void
  clearHistory: () => void
  animationSpeed: number
  animationState: AnimationState
  animationSide: { prev: CoinflipChoice; current: CoinflipChoice }
  setAnimationSide: (side: CoinflipChoice) => void
  startAnimation: () => void
  stopAnimation: () => void
  dispatch: () => void
  setDenom: (denom: string) => void
  view: {
    history: Array<UserCoice & { id: string }>
    isDrawing: boolean
    status: GameStatus
    roundState: RoundState
  }
  form: {
    errors: Array<string>
    fields: FormFields
    isDirty: boolean
  }
  setValue: <K extends keyof FormFields>(key: K, value: FormFields[K], setDirty?: boolean) => void

  setValues: <K extends keyof FormFields>({ }: Partial<Record<K, { value: FormFields[K]; setDirty: boolean }>>) => void
}

type ComputedStore = {
  gameSteps: Array<{ multiplier: string; payable: BigNumber; payableFormat: BigNumber; isLast: boolean }>
}

type RoundState = {
  currentStep: number
  userChoices: Array<UserCoice>
}

export type UserCoice = {
  choice: CoinflipChoice
  result: MiniGameStatus
}

export const calculateMultipliers = ({
  maxGameRound,
  rtp,
  denom,
  maxWin,
  amount,
}: {
  maxGameRound: number
  rtp: number
  denom: string
  maxWin: BigNumber
  amount: string
}) => {
  const steps = Array.from({ length: maxGameRound }, (_v, k) => {
    const multiplier = Math.max(1, Math.pow(2, k + 1) * rtp)

    const betValue = Number(amount) ? new BigNumberInBase(amount) : new BigNumberInBase(1)

    const betToken = getTokenByDenom(denom)
    const bet = betValue.toWei(betToken.decimals).multipliedBy(multiplier)

    const payable = BigNumberInBase.min(bet, maxWin)

    return {
      multiplier: multiplier.toFixed(2),
      payable: payable,
      payableFormat: new BigNumberInWei(payable).toBase(betToken.decimals),
      isLast: payable.isGreaterThanOrEqualTo(maxWin),
    }
  })

  let lastStep = steps.findIndex((x) => x.isLast)
  lastStep = lastStep === -1 ? maxGameRound - 1 : lastStep

  return [
    { multiplier: '0.00', payable: new BigNumber(0), payableFormat: new BigNumber(0), isLast: false },
    ...steps.slice(0, lastStep + 1),
  ]
}

const computed = createComputed(
  (state: CoinflipState): ComputedStore => {
    return {
      gameSteps: calculateMultipliers({
        amount: state.form.fields.amount,
        denom: state.form.fields.denom,
        maxGameRound: state.gameConfig.maxGameRound,
        maxWin: state.gameConfig.maxWin,
        rtp: state.gameConfig.rtp,
      }),
    }
  },
  { disableProxy: true, keys: ['form', 'gameConfig'] }
)

const intital = {
  gameId: null,
  gameConfig: {
    rtp: 0.97,
    maxWin: new BigNumberInBase(100000000).toWei(18),
    maxGameRound: 15,
  },
  bet: null,
  status: GameStatus.IDLE,
  roundState: { currentStep: 0, userChoices: [] },
  isDrawing: false,
}

export const useCoinflipStore = create<CoinflipState>()(
  computed((set, get) => ({
    gameId: null,
    gameConfig: intital.gameConfig,
    isInitialized: false,
    status: GameStatus.IDLE,
    roundState: { currentStep: 0, userChoices: [] },
    isDrawing: false,
    animationSpeed: 1,
    animationState: AnimationState.IDLE,
    animationSide: { prev: CoinflipChoice.Heads, current: CoinflipChoice.Heads },
    isWithdrawing: false,
    history: [],
    getHistory: () => get().history,
    lastRoundResult: undefined,
    addToHistory: (lastChoice) => set((state) => ({ history: [lastChoice, ...state.history].slice(0, 20) })),
    clearHistory: () => set((state) => ({ history: [], view: { ...state.view, history: [] } })),
    setWithdrawing: () => set({ isWithdrawing: true, isDrawing: true }),
    setWithdrawingOff: () => set({ isWithdrawing: false, isDrawing: false }),
    setDrawing: () => set((state) => ({ isDrawing: true, view: { ...state.view, isDrawing: true } })),
    setDrawingOff: () => set({ isDrawing: false }),
    setGameId: (gameId: string) => set({ gameId }),
    setAnimationSide: (side) =>
      set((state) => ({ animationSide: { prev: state.animationSide.current, current: side } })),
    startAnimation: () => set({ animationState: AnimationState.STARTED }),
    stopAnimation: () => set({ animationState: AnimationState.IDLE }),
    setError: () => set({ animationState: AnimationState.ERROR, isDrawing: false }),
    setGameActive: () =>
      set((state) => ({ status: GameStatus.ACTIVE, view: { ...state.view, status: GameStatus.ACTIVE } })),
    setNextStep: (step, choice) =>
      set((state) => ({
        roundState: {
          currentStep: step,
          userChoices: [...(state.roundState?.userChoices || []), { choice, result: MiniGameStatus.Pending }],
        },
      })),
    handleEndGame: () =>
      set((state) => ({
        ...intital,
        gameConfig: {
          ...state.gameConfig,
          maxGameRound: intital.gameConfig.maxGameRound,
        },
      })),
    boot: () =>
      set({
        ...intital,
        isInitialized: false,
        animationState: AnimationState.IDLE,
        animationSide: { prev: CoinflipChoice.Heads, current: CoinflipChoice.Heads },
      }),
    resumeGame: ({ gameId, rounds, lastChoice, gameConfig }) => {
      set({
        isInitialized: true,
        status: GameStatus.ACTIVE,
        gameId,
        gameConfig,
        roundState: rounds,
        animationSide: { current: lastChoice, prev: CoinflipChoice.Heads },
      })
      get().dispatch()
    },
    initialize: () => set({ ...intital, isInitialized: true }),
    dispatch: () =>
      set((state) => ({
        view: {
          history: state.history,
          isDrawing: state.isDrawing,
          status: state.status,
          roundState: state.roundState,
        },
      })),

    view: {
      history: [],
      isDrawing: false,
      status: GameStatus.IDLE,
      roundState: { currentStep: 0, userChoices: [] },
    },
    form: {
      errors: [],
      fields: {
        amount: '0.1',
        mode: GAME_MODE.INSTANT,
        denom: getTokenByCurrency(SupportedCurrencies.INJ).denom,
        minValue: currenciesSettings.minValue[SupportedCurrencies.INJ],
        maxValue: currenciesSettings.maxValue[GAME_MODE.INSTANT][SupportedCurrencies.INJ],
      },
      isDirty: false,
    },
    setDenom: (denom: string) => {
      const minValue = currenciesSettings.minValue[getTokenByDenom(denom).currency]

      set((state) => {
        const { fields, errors } = formValidation({
          amount: (minValue * 10).toString(),
          //parseFloat(state.form.fields.amount) < minValue ? minValue.toString() : state.form.fields.amount,
          denom: denom,
          mode: state.form.fields.mode,
          minValue: state.form.fields.minValue,
          maxValue: state.form.fields.maxValue,
        })

        return {
          form: {
            errors,
            fields,
            isDirty: true,
          },
        }
      })
    },
    setValue: (key, value, setDirty = true) => {
      get().setValues({
        [key]: {
          value: value as any,
          setDirty,
        },
      })
    },
    formRevalidation: () => {
      const { fields, errors } = formValidation(get().form.fields)
      set({
        form: {
          fields,
          errors,
          isDirty: true,
        },
      })
    },
    setValues: (values) => {
      set((state) => {
        let newFields = {
          ...state.form.fields,
        }

        let isDirty = false
        for (const key in values) {
          newFields = {
            ...newFields,
            [key]: values[key]!.value,
          }

          isDirty = isDirty || values[key]!.setDirty
        }

        const { fields, errors } = formValidation(formParser(newFields))

        sessionStorage.setItem('coinflip-form', JSON.stringify(fields))

        return {
          form: {
            errors,
            fields,
            isDirty: !isDirty ? state.form.isDirty : true,
          },
        }
      })
    },
  }))
)

const formParser = (values: FormFields) => {
  const { amount } = values
  const token = getTokenByDenom(values.denom)
  const fields = {
    amount,
    minValue: currenciesSettings.minValue[token.currency]!,
    maxValue: currenciesSettings.maxValue[values.mode][token.currency]!,
  }

  if (isNaN(Number(amount)) || Number(amount) < 0) {
    fields.amount = ''
    return { ...values, ...fields }
  }

  if (Number(amount) > fields.maxValue) {
    fields.amount = fields.maxValue.toString()
  }

  const regex = new RegExp(`^\\d+(\\.\\d{0,${token.precisions}})?$`, 'g')

  if (amount != '' && !regex.test(amount)) {
    fields.amount = Number(amount).toFixed(token.precisions)
  }

  return { ...values, ...fields }
}

const formValidation = (fields: FormFields) => {
  let { amount, minValue } = fields

  const errors = []

  if (amount.length === 0) {
    errors.push('Amount is required')
  }

  if (Number(amount) < minValue) {
    errors.push('Min amount is ' + minValue)
  }

  return {
    fields,
    errors,
  }
}
